import { SwapOutlined } from '@ant-design/icons';
import { Modal, Card, Spin, Empty, App } from 'antd';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';
import { useIntl, history } from '@umijs/max';
import { getAdminList, switchAdmin } from '@/services/ant-design-pro/login';

const useStyles = createStyles(({ token }) => {
  return {
    roleCard: {
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginBottom: 16,
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: token.boxShadowSecondary,
      },
    },
    currentRole: {
      borderColor: token.colorPrimary,
      borderWidth: 2,
      backgroundColor: token.colorPrimaryBg,
    },
    roleCardGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 16,
    },
  };
});

interface SwitchAdminModalProps {
  visible: boolean;
  onCancel: () => void;
}

const SwitchAdminModal: React.FC<SwitchAdminModalProps> = ({ visible, onCancel }) => {
  const { styles, cx } = useStyles();
  const { message } = App.useApp();
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [admins, setAdmins] = useState<API.AdminInfo[]>([]);
  const [currentAdminId, setCurrentAdminId] = useState<number>();

  // 加载管理员身份列表
  const loadAdminList = async () => {
    try {
      setLoading(true);
      const result = await getAdminList();

      if (result.code === 200 && result.data) {
        const currentId = result.data.currentAdminId;
        const adminList = result.data.admins;

        // 将当前身份排在第一位
        const sortedAdmins = adminList.sort((a, b) => {
          if (a.adminId === currentId) return -1;
          if (b.adminId === currentId) return 1;
          return 0;
        });

        setAdmins(sortedAdmins);
        setCurrentAdminId(currentId);
      } else {
        message.error(result.message || intl.formatMessage({ id: 'pages.switchAdmin.switchFailed' }));
      }
    } catch (error) {
      console.error('获取管理员身份列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 切换管理员身份
  const handleSwitch = async (adminId: number) => {
    // 不能切换到当前身份
    if (adminId === currentAdminId) {
      message.warning(intl.formatMessage({ id: 'pages.switchAdmin.currentRole' }));
      return;
    }

    try {
      setSwitching(true);
      const result = await switchAdmin({ adminId });

      if (result.code === 200 && result.data) {
        const { token, adminInfo } = result.data;

        // 更新本地存储
        localStorage.setItem('auth_token', token);

        message.success(intl.formatMessage({ id: 'pages.switchAdmin.switchSuccess' }));

        // 关闭对话框
        onCancel();

        // 延迟跳转到首页，让用户看到成功消息
        // 跳转到根路径，RootRedirect 组件会自动根据新身份的权限重定向到第一个有权限的页面
        setTimeout(() => {
          history.push('/');
          // 刷新页面以重新加载用户信息和菜单
          window.location.reload();
        }, 500);
      } else {
        message.error(result.message || intl.formatMessage({ id: 'pages.switchAdmin.switchFailed' }));
      }
    } catch (error) {
      console.error('切换身份失败:', error);
    } finally {
      setSwitching(false);
    }
  };

  // 对话框打开时加载数据
  useEffect(() => {
    if (visible) {
      loadAdminList();
    }
  }, [visible]);

  // 身份类型映射
  const getAdminTypeText = (adminType: string) => {
    // 将 adminType 转换为国际化 key 格式
    // PLATFORM -> platform, BRAND_ADMIN -> brand, STORE_ADMIN -> store, FUND_ACCOUNT -> fund
    let typeKey = adminType.toLowerCase();
    if (typeKey === 'platform_admin') typeKey = 'platform';
    if (typeKey === 'brand_admin') typeKey = 'brand';
    if (typeKey === 'store_admin') typeKey = 'store';
    if (typeKey === 'fund_account') typeKey = 'fund';

    const key = `pages.switchAdmin.adminType.${typeKey}`;
    return intl.formatMessage({ id: key, defaultMessage: adminType });
  };

  return (
    <Modal
      title={
        <span>
          <SwapOutlined style={{ marginRight: 8 }} />
          {intl.formatMessage({ id: 'pages.switchAdmin.modalTitle' })}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnHidden
      maskClosable={false}
    >
      <Spin spinning={loading || switching}>
        <div style={{ minHeight: 200 }}>
          {admins.length === 0 && !loading ? (
            <Empty description={intl.formatMessage({ id: 'pages.switchAdmin.noAdmins' })} />
          ) : (
            <div className={styles.roleCardGrid}>
              {admins.map((admin) => {
                const isCurrent = admin.adminId === currentAdminId;
                return (
                  <Card
                    key={admin.adminId}
                    className={cx(styles.roleCard, isCurrent && styles.currentRole)}
                    hoverable={!isCurrent}
                    onClick={() => !isCurrent && handleSwitch(admin.adminId)}
                  >
                    <Card.Meta
                      title={
                        <span>
                          {admin.adminTypeDesc}
                          {isCurrent && (
                            <span style={{ color: '#1890ff', marginLeft: 8, fontSize: 12 }}>
                              ({intl.formatMessage({ id: 'pages.switchAdmin.currentRole' })})
                            </span>
                          )}
                        </span>
                      }
                      description={
                        <div style={{ fontSize: 13 }}>
                          {admin.name && (
                            <div>
                              {intl.formatMessage({ id: 'pages.switchAdmin.adminName' })}: {admin.name}
                            </div>
                          )}
                          <div>
                            {intl.formatMessage({ id: 'pages.switchAdmin.adminTypeLabel' })}:{' '}
                            {getAdminTypeText(admin.adminType)}
                          </div>
                          {admin.brandName && (
                            <div>
                              {intl.formatMessage({ id: 'pages.switchAdmin.brand' })}: {admin.brandName}
                            </div>
                          )}
                          {admin.storeName && (
                            <div>
                              {intl.formatMessage({ id: 'pages.switchAdmin.store' })}: {admin.storeName}
                            </div>
                          )}
                        </div>
                      }
                    />
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default SwitchAdminModal;
